import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import toast from 'react-hot-toast';
import StaffLink from '../../components/shared/StaffLink';
import {
  CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline';

const TYPE_STYLE = {
  birthday:        { color: 'bg-pink-100 text-pink-800 border-pink-200',         dot: 'bg-pink-500',     label: '🎂' },
  anniversary:     { color: 'bg-purple-100 text-purple-800 border-purple-200',   dot: 'bg-purple-500',   label: '🎉' },
  holiday:         { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', dot: 'bg-emerald-500', label: '🎄' },
  school_closure:  { color: 'bg-red-100 text-red-800 border-red-200',            dot: 'bg-red-500',     label: '🚫' },
  time_off:        { color: 'bg-amber-100 text-amber-800 border-amber-200',      dot: 'bg-amber-500',   label: '🌴' },
  compliance:      { color: 'bg-orange-100 text-orange-800 border-orange-200',   dot: 'bg-orange-500',  label: '📋' },
  hire_start:      { color: 'bg-blue-100 text-blue-800 border-blue-200',         dot: 'bg-blue-500',    label: '🌟' },
  probation_end:   { color: 'bg-teal-100 text-teal-800 border-teal-200',         dot: 'bg-teal-500',    label: '✅' },
};
const TYPE_NAME = {
  birthday: 'Birthday',
  anniversary: 'Work Anniversary',
  holiday: 'Holiday',
  school_closure: 'Closure',
  time_off: 'Time Off',
  compliance: 'Compliance Expiry',
  hire_start: 'New Hire',
  probation_end: 'Off Probation',
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => {
  const x = new Date(d);
  x.setMonth(x.getMonth() + n);
  return x;
};
const formatISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default function CalendarPage() {
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState(new Set(Object.keys(TYPE_STYLE)));
  const [selected, setSelected] = useState(null); // selected day for detail panel

  const monthStart = useMemo(() => startOfMonth(cursor), [cursor]);
  const monthEnd = useMemo(() => {
    const d = new Date(monthStart);
    d.setMonth(d.getMonth() + 1);
    d.setDate(0); // last day of month
    return d;
  }, [monthStart]);

  // Pad to a full grid: start at the Sunday on/before monthStart, end at the
  // Saturday on/after monthEnd. Up to 6 weeks.
  const gridStart = useMemo(() => {
    const d = new Date(monthStart);
    d.setDate(d.getDate() - d.getDay());
    return d;
  }, [monthStart]);
  const gridEnd = useMemo(() => {
    const d = new Date(monthEnd);
    d.setDate(d.getDate() + (6 - d.getDay()));
    return d;
  }, [monthEnd]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/calendar', {
        params: { from: formatISO(gridStart), to: formatISO(gridEnd) },
      });
      setEvents(data.events || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load calendar');
    } finally { setLoading(false); }
  }, [gridStart, gridEnd]);

  useEffect(() => { load(); }, [load]);

  // Group events by date for fast lookup.
  const byDate = useMemo(() => {
    const m = {};
    for (const e of events) {
      if (!typeFilter.has(e.type)) continue;
      if (!m[e.date]) m[e.date] = [];
      m[e.date].push(e);
    }
    return m;
  }, [events, typeFilter]);

  // Build the day grid (rows of 7).
  const days = [];
  for (let d = new Date(gridStart); d <= gridEnd; d.setDate(d.getDate() + 1)) {
    days.push(new Date(d));
  }
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));

  const toggleType = (t) => {
    setTypeFilter(prev => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  const todayISO = formatISO(new Date());
  const monthLabel = monthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="animate-slide-up">
      <header className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarDaysIcon className="w-6 h-6" /> Calendar
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Birthdays, anniversaries, holidays, closures, and approved time off — all center-wide.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCursor(addMonths(monthStart, -1))}
            className="p-2 rounded-lg hover:bg-gray-100"><ChevronLeftIcon className="w-4 h-4" /></button>
          <p className="text-sm font-semibold text-gray-900 min-w-[140px] text-center">{monthLabel}</p>
          <button onClick={() => setCursor(addMonths(monthStart, 1))}
            className="p-2 rounded-lg hover:bg-gray-100"><ChevronRightIcon className="w-4 h-4" /></button>
          <button onClick={() => setCursor(startOfMonth(new Date()))}
            className="text-xs text-gray-500 hover:text-orange-500 ml-2">Today</button>
        </div>
      </header>

      {/* Type legend / filter chips */}
      <div className="flex gap-1 flex-wrap mb-4">
        {Object.entries(TYPE_STYLE).map(([t, s]) => (
          <button key={t} onClick={() => toggleType(t)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border inline-flex items-center gap-1 ${
              typeFilter.has(t) ? s.color : 'bg-gray-100 text-gray-400 border-gray-200 line-through'
            }`}>
            <span className={`w-2 h-2 rounded-full ${s.dot}`} />
            {TYPE_NAME[t]}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* Month grid */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
          ) : (
            <table className="w-full table-fixed">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                    <th key={d} className="px-2 py-2 text-xs font-semibold text-gray-500 uppercase text-left">{d}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weeks.map((week, wi) => (
                  <tr key={wi} className="border-b border-gray-100 last:border-b-0">
                    {week.map((d, di) => {
                      const iso = formatISO(d);
                      const inMonth = d.getMonth() === monthStart.getMonth();
                      const isToday = iso === todayISO;
                      const dayEvents = byDate[iso] || [];
                      return (
                        <td key={di} onClick={() => setSelected(iso)}
                          className={`align-top px-1.5 py-1.5 h-24 cursor-pointer hover:bg-orange-50/30 transition border-r border-gray-50 last:border-r-0 ${
                            !inMonth ? 'bg-gray-50/40' : ''
                          } ${selected === iso ? 'bg-orange-50/50' : ''}`}>
                          <div className={`text-xs font-semibold mb-1 inline-flex items-center justify-center ${
                            isToday
                              ? 'w-5 h-5 rounded-full bg-orange-500 text-white'
                              : inMonth ? 'text-gray-800' : 'text-gray-300'
                          }`}>
                            {d.getDate()}
                          </div>
                          <div className="space-y-0.5">
                            {dayEvents.slice(0, 3).map((e, i) => (
                              <div key={i} className={`text-[10px] px-1.5 py-0.5 rounded truncate ${TYPE_STYLE[e.type]?.color || 'bg-gray-100'}`}
                                title={e.label}>
                                {e.label}
                              </div>
                            ))}
                            {dayEvents.length > 3 && (
                              <div className="text-[10px] text-gray-500">+{dayEvents.length - 3} more</div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Side panel: details for selected day, or "upcoming" */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          {selected ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-900">
                  {new Date(selected + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
                <button onClick={() => setSelected(null)} className="text-xs text-gray-400 hover:text-gray-700">Clear</button>
              </div>
              {(byDate[selected] || []).length === 0 ? (
                <p className="text-xs text-gray-500">Nothing scheduled.</p>
              ) : (
                <div className="space-y-2">
                  {(byDate[selected] || []).map((e, i) => <EventDetail key={i} event={e} />)}
                </div>
              )}
            </>
          ) : (
            <UpcomingPanel events={events} byDate={byDate} todayISO={todayISO} />
          )}
        </div>
      </div>
    </div>
  );
}

function EventDetail({ event }) {
  const style = TYPE_STYLE[event.type] || {};
  return (
    <div className={`rounded-xl border px-3 py-2 ${style.color}`}>
      <div className="flex items-center gap-2 text-xs font-semibold mb-0.5">
        <span className={`w-2 h-2 rounded-full ${style.dot}`} />
        {TYPE_NAME[event.type]}
      </div>
      <p className="text-sm text-gray-900">
        {event.staff_id ? (
          <StaffLink id={event.staff_id} name={event.staff_name} />
        ) : event.label}
      </p>
      {event.type === 'anniversary' && <p className="text-xs text-gray-600 mt-0.5">{event.years}-year work anniversary</p>}
      {event.type === 'time_off' && !event.full_day && (
        <p className="text-xs text-gray-600 mt-0.5">{event.start_time?.slice(0,5)} – {event.end_time?.slice(0,5)}</p>
      )}
      {event.type === 'time_off' && event.reason && (
        <p className="text-xs text-gray-600 italic mt-0.5">"{event.reason}"</p>
      )}
      {event.notes && <p className="text-xs text-gray-600 mt-0.5">{event.notes}</p>}
    </div>
  );
}

function UpcomingPanel({ events, byDate, todayISO }) {
  const upcoming = events
    .filter(e => e.date >= todayISO)
    .slice(0, 12);
  return (
    <>
      <p className="text-sm font-semibold text-gray-900 mb-3">Upcoming</p>
      {upcoming.length === 0 ? (
        <p className="text-xs text-gray-500">Nothing on the calendar this month.</p>
      ) : (
        <div className="space-y-2">
          {upcoming.map((e, i) => {
            const style = TYPE_STYLE[e.type] || {};
            return (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${style.dot}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-gray-500">{new Date(e.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                  <p className="text-gray-900 truncate">{e.label}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-[11px] text-gray-400 mt-3 leading-relaxed">
        Click any day on the calendar for details. Birthdays + anniversaries come from each staff's profile —{' '}
        <Link to="/staff" className="text-orange-500 hover:underline">add dates</Link> on the contact tab.
      </p>
    </>
  );
}
